import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The lighthouse IS the rig: one source,
// two beams, and everything else in the picture exists only where they pass.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'lighthouse',
  family: 'photographic',
  danceability: 0.28,
  energyLevel: 0.25,
  bpmMin: 50,
  bpmMax: 130,
  movement: 0.30,
  tonality: 0.90,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.50] },
  enabled: true
};

// Lighthouse: a lighthouse on a cliff at night, in fog, whose beam turns
// once a bar and sweeps the mist, the rain and the waves.
//
// THE BEAM SHOWS ONLY WHAT IT CROSSES. Between two passes the picture is
// nearly black. A beam in fog does not light a scene: it reveals the
// MATTER OF THE AIR - mist, rain and spray exist on screen only inside the
// cone, and the cliff, the sea and the tower are silhouettes lit by the
// sweep alone. Every material here evaluates the same reveal: for a point
// p, the angle between (p - lantern) and each beam's axis against the
// cone's half-angle, times the fog's extinction over the distance from the
// lantern. LaserCathedral's dust does the same test against a laser line;
// here it is a cone, and there are two of them, back to back.
//
// ONE TURN PER BAR, exactly, by construction: the beam's azimuth is
// 2 pi x barPhase. The first beam points at the camera at phase 0, so the
// glare - flare, halo, the whole frame lifting - lands on beat one. The
// second beam is fainter and passes the camera on beat three, a lesser
// flash. Without a grid the turn takes 4 s.

// --- the scene -----------------------------------------------------------------
const CAM_POS = [0, 1.6, 0];
const CAM_LOOK = [0, 5.0, -50];
// The cliff's face stands eight units IN FRONT of the tower and rises TALL
// to the left of it. The geometry of the reveal: a beam tilted eighteen
// degrees down from a lantern nineteen units up crosses a plane eight
// units ahead at a height that falls with the azimuth (straight ahead it is
// still sixteen units up; sixty degrees round it is fourteen, ninety it is
// four). A face in the tower's own plane is never crossed at all, and a
// headland running towards the camera recedes from the beam - both tried,
// both black. With a ridge climbing to nineteen within fifteen units of the
// tower, every beam turned past sixty degrees draws a streak down the rock.
const CLIFF_Z = -42;
const TOWER_Z = -50;
const LIGHTHOUSE_X_FRAC = 0.30;         // of the frame's width, at the cliff's depth
const CLIFF_TOP_Y = 8.0;                // the plateau the tower stands on
const TOWER_H = 10.0;
const TOWER_BASE_R = 1.6;
const TOWER_TOP_R = 1.1;
const LANTERN_H = 1.9;                  // the lantern room above the gallery
const SEA_NEAR = -1.5, SEA_FAR = -75;
const SEA_COLS = 96, SEA_ROWS = 40;
const SEA_SWELL = 0.55;                 // base wave amplitude, world units
const FOAM_FROM = 0.62;                 // normalised height where foam starts

// --- the beams -----------------------------------------------------------------
const CONE_HALF_DEG = 8.0 / 2;          // an 8 degree cone
const HORN_HALF_DEG = 14.0 / 2;         // the fog horn: 14 degrees for two bars
const HORN_BARS = 2;
const BEAM_LENGTH = 150;
// The beam's tilt is not a constant: at azimuth zero it points AT the
// camera, so the tilt is whatever the lantern-to-camera line makes (about
// sixteen degrees down). That is what puts the camera inside the cone on
// beat one, with the mist and rain in front of the lens lit up.
const SECOND_BEAM = 0.55;               // the back beam, fainter
const CONE_OPACITY = 0.14;
const FREE_TURN_SECONDS = 4.0;          // without a grid
const GLARE_HALF_DEG = 12;              // how far off the camera the glare still reads
// The reveal's extinction: the fog's density is the picture's, but the
// beam's in-scattering falls slower than pure extinction over these
// distances (fifty units to the cliff), or nothing past the lantern shows.
const REVEAL_EXT = 0.35;

// --- the fog -------------------------------------------------------------------
const FOG_START = 0.08;                 // FogExp2 density at the start of the track
const FOG_END = 0.028;                  // it lifts to this by the end
const AMBIENT_END = 0.11;               // what the cliff shows outside the beam once the fog has lifted

// --- the particles -------------------------------------------------------------
const TIER_LAYOUT = {
  0: { mist: 800, rain: 400, beams: 1, seaMoves: true, spray: 200 },
  1: { mist: 1600, rain: 900, beams: 2, seaMoves: false, spray: 300 },
  2: { mist: 3000, rain: 1500, beams: 2, seaMoves: true, spray: 400 }
};
// The boxes are SMALL on purpose: a beam eight degrees wide holds a few
// hundred cubic units at any distance, and particles spread over the whole
// picture put a dozen of them in it. Packed into the volume the beams
// actually sweep between the tower and the camera, the same count fills
// the cone.
const MIST_BOX = { x: 40, y0: 0.3, y1: 15, z0: -4, z1: -58 };
const RAIN_BOX = { x: 16, y1: 15, z0: -1.0, z1: -24 };
const RAIN_FALL = 11;                   // world units per second
const RAIN_WIND = 0.35;                 // sideways, as a fraction of the fall
const SPRAY_SECONDS = 3.2;
const SAFE_FLOOR = 0.55;

const _size = new THREE.Vector2();
const _v3 = new THREE.Vector3();
const _v3b = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };
const DOWN = new THREE.Vector3(0, -1, 0);

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function lcg(seed) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

// --- shaders ---------------------------------------------------------------------

// The reveal, shared by everything: how much of the beams a world point sits
// in, with the fog's extinction from the lantern. uBeam0/1 are unit axes,
// uBeamGain their strengths (0 when a tier has one beam), uConeCos the
// cosines of the cone's inner and outer edges (soft rim).
const REVEAL_CHUNK = `
  uniform vec3 uLantern;
  uniform vec3 uBeam0;
  uniform vec3 uBeam1;
  uniform vec2 uBeamGain;
  uniform vec2 uConeCos;      // x = cos(outer), y = cos(inner)
  uniform float uFog;
  float revealAt(vec3 p) {
    vec3 v = p - uLantern;
    float dist = max(length(v), 0.001);
    vec3 dir = v / dist;
    float c0 = dot(dir, uBeam0), c1 = dot(dir, uBeam1);
    float in0 = smoothstep(uConeCos.x, uConeCos.y, c0) * uBeamGain.x;
    float in1 = smoothstep(uConeCos.x, uConeCos.y, c1) * uBeamGain.y;
    float ext = exp(-uFog * ${REVEAL_EXT.toFixed(2)} * dist) / (1.0 + dist / 60.0);
    return max(in0, in1) * ext;
  }
`;

// Safe zone at the brief's floor, from the shared uniforms.
const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const hashGLSL = `
  float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
`;

// The sky: near black, a shade of the fog's own grey towards the horizon.
const skyVertexShader = `
  precision mediump float;
  varying vec2 vNdc;
  void main() { vNdc = position.xy; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const skyFragmentShader = `
  precision mediump float;
  uniform float uHorizon;      // ndc y of the horizon
  uniform float uLift;         // 1 - fog normalised: the sky clears a little as it lifts
  uniform float uGlare;
  uniform vec3 uTint;
  varying vec2 vNdc;
  void main() {
    float up = clamp((vNdc.y - uHorizon) / (1.0 - uHorizon), 0.0, 1.0);
    vec3 top = vec3(0.004, 0.006, 0.012);
    vec3 low = vec3(0.020, 0.024, 0.034) * (0.7 + 0.5 * uLift);
    vec3 col = mix(low, top, pow(up, 0.7));
    // The glare: the whole frame lifts, briefly, towards the beam's tint.
    col += uTint * uGlare * 0.05;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The sea: a low-resolution grid, its height a sum of sines in the vertex
// shader (the lows set the swell), dark, lit only by the beams, with foam
// on the crests that the beam picks out.
const seaVertexShader = `
  precision mediump float;
  uniform float uTime;
  uniform float uSwell;
  varying vec3 vWorld;
  varying float vCrest;
  varying float vSafe;
  ${SAFE_CHUNK}
  float h(vec2 p, float t) {
    return sin(p.x * 0.31 + t * 0.9) * 0.45 + sin(p.y * 0.23 - t * 0.7 + p.x * 0.05) * 0.55
         + sin((p.x + p.y) * 0.17 + t * 0.55) * 0.3 + sin(p.y * 0.61 - t * 1.3 + 1.0) * 0.18
         + sin(p.x * 0.9 + p.y * 0.4 + t * 1.7) * 0.08;
  }
  void main() {
    vec3 p = position;
    float hh = h(p.xz, uTime);
    p.y = hh * uSwell;
    vCrest = clamp((hh + 1.5) / 3.1, 0.0, 1.0);
    vWorld = p;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
    gl_Position = clip;
    vSafe = safeAt(clip);
  }
`;
const seaFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAmbient;
  uniform vec3 uCamera;
  varying vec3 vWorld;
  varying float vCrest;
  varying float vSafe;
  ${REVEAL_CHUNK}
  ${hashGLSL}
  void main() {
    float r = revealAt(vWorld);
    float foam = smoothstep(${FOAM_FROM.toFixed(2)}, 0.95, vCrest);
    float grain = 0.85 + 0.3 * hash21(floor(vWorld.xz * 2.0));
    // A moving highlight: the water facing the beam and the camera.
    vec3 toCam = normalize(uCamera - vWorld);
    float glint = 0.35 + 0.65 * pow(max(toCam.y, 0.0), 0.6);
    float crest = 0.25 + 0.75 * vCrest * vCrest;
    vec3 water = uTint * (0.06 + 0.30 * glint * crest) * grain;
    vec3 col = (water + uTint * foam * 1.1) * r + uTint * foam * uAmbient * 0.6 + vec3(0.004, 0.006, 0.010);
    gl_FragColor = vec4(col * vSafe, 1.0);
  }
`;

// The cliff and the tower: silhouettes. Lit by the beams where they pass,
// with a rock grain, and a little ambient once the fog has lifted.
const rockVertexShader = `
  precision mediump float;
  attribute float aKind;       // 0 rock, 1 tower, 2 lantern room
  varying vec3 vWorld;
  varying float vKind;
  varying vec2 vLocal;
  void main() {
    vWorld = position;
    vKind = aKind;
    vLocal = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const rockFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAmbient;
  uniform float uGlare;
  uniform float uLanternGlow;
  varying vec3 vWorld;
  varying float vKind;
  varying vec2 vLocal;
  ${REVEAL_CHUNK}
  ${hashGLSL}
  void main() {
    float r = revealAt(vWorld);
    vec3 col;
    if (vKind < 0.5) {
      float grain = 0.55 + 0.45 * hash21(floor(vWorld.xy * 1.7));
      float strata = 0.8 + 0.2 * sin(vWorld.y * 2.1 + vWorld.x * 0.3);
      col = vec3(0.30, 0.28, 0.26) * grain * strata * (r * 1.8 + uAmbient);
    } else if (vKind < 1.5) {
      // The tower: white with red bands, suggested when the glare lifts the
      // frame and by the lantern's own spill down the wall.
      float band = step(0.5, fract(vLocal.y * 4.0 + 0.25));
      vec3 paint = mix(vec3(0.85, 0.82, 0.78), vec3(0.55, 0.10, 0.08), band);
      float light = uGlare * 0.30 + uLanternGlow * 0.10 * smoothstep(0.55, 1.0, vLocal.y) + uAmbient * 0.6;
      col = paint * light * (0.8 + 0.2 * vLocal.x);
    } else {
      col = uTint * (0.015 + 0.04 * uLanternGlow) + vec3(0.02) * uGlare;
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The beam cones (StageLightRig's look: an open cone from the apex, the
// alpha fading along the length and at the silhouette), with the fog's
// extinction and the safe-zone floor.
const coneVertexShader = `
  precision mediump float;
  varying float vAlong;        // 0 apex .. 1 base
  varying vec3 vNormalV;
  varying vec3 vViewDir;
  varying float vSafe;
  varying float vDist;
  varying vec3 vLocal;
  ${SAFE_CHUNK}
  void main() {
    vAlong = clamp(-position.y / ${BEAM_LENGTH.toFixed(1)}, 0.0, 1.0);
    vDist = -position.y;
    vLocal = position;
    vNormalV = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mv.xyz);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    vSafe = safeAt(clip);
  }
`;
const coneFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uOpacity;
  uniform float uFogCone;
  uniform float uTime;
  uniform float uFacing;       // 0 when the beam is on the camera: the glare takes over
  varying float vAlong;
  varying vec3 vNormalV;
  varying vec3 vViewDir;
  varying float vSafe;
  varying float vDist;
  varying vec3 vLocal;
  float hash13(vec3 p3) { p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
  float noise3D(vec3 p) {
    vec3 i = floor(p); vec3 f = fract(p); f = f * f * (3.0 - 2.0 * f);
    float n000 = hash13(i), n100 = hash13(i + vec3(1.0, 0.0, 0.0)), n010 = hash13(i + vec3(0.0, 1.0, 0.0)), n110 = hash13(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash13(i + vec3(0.0, 0.0, 1.0)), n101 = hash13(i + vec3(1.0, 0.0, 1.0)), n011 = hash13(i + vec3(0.0, 1.0, 1.0)), n111 = hash13(i + vec3(1.0, 1.0, 1.0));
    return mix(mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y), mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y), f.z);
  }
  void main() {
    // StageLightRig's beam: patchy density, the silhouette fading where the
    // surface is edge-on, and the fog eating the far end.
    float edge = smoothstep(0.0, 0.30, abs(dot(normalize(vNormalV), normalize(vViewDir))));
    float n = noise3D(vLocal * 0.12 + vec3(0.0, uTime * 0.25, 0.0));
    float density = clamp(0.4 + n * 1.1, 0.0, 1.4);
    float ext = exp(-uFogCone * vDist);
    float along = (1.0 - smoothstep(0.45, 1.0, vAlong)) * (0.25 + 0.75 * ext) * smoothstep(0.0, 0.03, vAlong);
    float alpha = uOpacity * along * density * edge * vSafe * uFacing;
    gl_FragColor = vec4(uTint, alpha);
  }
`;

// The revealed particles: mist (soft, slow, large) and rain (fine, fast,
// oblique). Invisible outside the cone.
const particleVertexShader = `
  precision mediump float;
  attribute float aSeed;
  attribute vec3 aBase;
  uniform float uTime;
  uniform float uKind;         // 0 mist, 1 rain, 2 spray
  uniform float uPixelScale;
  uniform float uPointMax;
  uniform float uSizeMul;
  uniform float uSprayT;
  uniform vec3 uSprayOrigin;
  uniform vec3 uCamera;
  varying float vReveal;
  varying float vSoft;
  ${REVEAL_CHUNK}
  ${SAFE_CHUNK}
  void main() {
    vec3 p = aBase;
    float soft = 1.0;
    if (uKind < 0.5) {
      // Mist: a slow drift on the wind, each puff on its own loop.
      p.x += sin(uTime * 0.11 + aSeed * 6.2831) * 2.5 + uTime * 0.35;
      p.x = mod(p.x + ${MIST_BOX.x.toFixed(1)}, ${(2 * MIST_BOX.x).toFixed(1)}) - ${MIST_BOX.x.toFixed(1)};
      p.y += sin(uTime * 0.17 + aSeed * 4.0) * 0.8;
      p.z += cos(uTime * 0.09 + aSeed * 3.1) * 1.5;
    } else if (uKind < 1.5) {
      // Rain: falling, oblique, looping through the box.
      float fall = uTime * ${RAIN_FALL.toFixed(1)} * (0.8 + 0.4 * aSeed);
      p.y = mod(aBase.y - fall, ${RAIN_BOX.y1.toFixed(1)});
      p.x = mod(aBase.x + fall * ${RAIN_WIND.toFixed(2)} + ${RAIN_BOX.x.toFixed(1)}, ${(2 * RAIN_BOX.x).toFixed(1)}) - ${RAIN_BOX.x.toFixed(1)};
      soft = 0.0;
    } else {
      // Spray: thrown up from the wave's break, falling back.
      float t = uSprayT;
      vec3 v = aBase;            // a velocity, for spray
      p = uSprayOrigin + v * t + vec3(0.0, -4.5 * t * t, 0.0);
      float life = 1.0 - smoothstep(0.0, ${SPRAY_SECONDS.toFixed(1)}, t);
      if (t < 0.0 || life <= 0.0 || p.y < 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 1.0; vReveal = 0.0; vSoft = 0.0; return; }
      soft = 0.5;
    }
    float r = revealAt(p);
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    float depth = max(-mv.z, 0.001);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    float worldSize = uKind < 0.5 ? (0.45 + aSeed * 0.8) : (uKind < 1.5 ? 0.05 : 0.18);
    gl_PointSize = clamp(worldSize * uPixelScale * uSizeMul / depth, uKind < 0.5 ? 2.0 : 1.6, uPointMax);
    // Nearer the camera a puff is thinner (it is in your face, not a wall).
    float near = smoothstep(2.0, 9.0, depth);
    vReveal = r * safeAt(clip) * mix(0.35, 1.0, near);
    vSoft = soft;
  }
`;
const particleFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uGain;
  varying float vReveal;
  varying float vSoft;
  void main() {
    if (vReveal < 0.003) discard;
    vec2 d = gl_PointCoord - 0.5;
    float rr = dot(d, d) * 4.0;
    float a = vSoft > 0.75 ? exp(-rr * 2.2) * 0.55 : (vSoft > 0.25 ? (1.0 - smoothstep(0.3, 1.0, rr)) : (1.0 - smoothstep(0.2, 1.0, rr)));
    gl_FragColor = vec4(uTint * a * vReveal * uGain, 1.0);
  }
`;

// The lantern: a Fresnel lens, concentric rings turning with the beam, a
// very bright point at the centre; and the glare - a cross flare and a
// halo, in screen space, when the beam faces the camera.
const lanternVertexShader = `
  precision mediump float;
  varying vec2 vLocal;
  void main() {
    vLocal = uv * 2.0 - 1.0;
    // Billboard: the quad faces the camera.
    vec4 centre = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    vec3 pos = centre.xyz + vec3(position.xy, 0.0);
    gl_Position = projectionMatrix * vec4(pos, 1.0);
  }
`;
const lanternFragmentShader = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAngle;
  uniform float uGlow;         // the lantern's own intensity (horn x2)
  uniform float uGlare;
  varying vec2 vLocal;
  void main() {
    float d = length(vLocal);
    // The lens: rings, and the two bright faces of the rotating optic.
    float rings = 0.5 + 0.5 * sin(d * 110.0);
    float lens = (1.0 - smoothstep(0.10, 0.14, d)) * (0.18 + 0.22 * rings);
    float a = atan(vLocal.y, vLocal.x);
    float faces = pow(max(cos(a - uAngle), 0.0), 6.0) + 0.55 * pow(max(cos(a - uAngle + 3.14159), 0.0), 6.0);
    lens += (1.0 - smoothstep(0.0, 0.14, d)) * faces * 0.6;
    float core = exp(-d * d * 400.0) * 2.4;
    float halo = exp(-d * d * 22.0) * 0.30;
    // The glare: a cross and a wide halo, only when the beam is on us.
    float cross = (exp(-abs(vLocal.y) * 70.0) + exp(-abs(vLocal.x) * 70.0)) * (1.0 - smoothstep(0.0, 1.0, d)) * 0.9;
    float glareHalo = exp(-d * d * 2.2) * 0.7;
    float rim = 1.0 - smoothstep(0.55, 0.97, d);   // nothing reaches the quad's edge
    vec3 col = uTint * ((lens + core + halo) * uGlow + (cross + glareHalo) * uGlare * 1.3) * rim;
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class LighthouseVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER_LAYOUT[this._tier] || TIER_LAYOUT[1];
    const fog = parseFloat(urlParam('lightfog'));
    this._forceFog = Number.isFinite(fog) ? fog : null;
    const beam = parseFloat(urlParam('lightbeam'));
    this._forceBeamDeg = Number.isFinite(beam) ? beam : null;
    this._debugHorn = urlParam('lighthorn') === '1';
    this._debugWave = urlParam('lightwave') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAM_POS[0], CAM_POS[1], CAM_POS[2]);
    camera.lookAt(CAM_LOOK[0], CAM_LOOK[1], CAM_LOOK[2]);
    camera.updateMatrixWorld();
    this._aspect = camera.aspect || 16 / 9;
    this._time = 0;
    this._beatSeconds = 0.5;
    this._lastBarPhase = null; this._lastBeatPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._turn = 0;
    this._pendingDropMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._hornUntil = -1e9; this._coneHalf = CONE_HALF_DEG; this._glow = 1;
    this._sprayAt = -1e9;
    this._glare = 0; this._glareTarget = 0;
    this._lastProgress = 0;
    this._tint = new THREE.Color(0.95, 0.88, 0.72);
    this._objects = []; this._materials = []; this._geometries = [];

    const halfW = -CLIFF_Z * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * this._aspect;
    this._lighthouseX = (LIGHTHOUSE_X_FRAC * 2 - 1) * halfW;
    this._halfW = halfW;
    this._lantern = new THREE.Vector3(this._lighthouseX, CLIFF_TOP_Y + TOWER_H + LANTERN_H * 0.55, TOWER_Z + 0.5);

    // The beam's base direction: at azimuth 0 it points at the camera, tilt
    // included.
    this._toCamera = new THREE.Vector3(CAM_POS[0] - this._lantern.x, CAM_POS[1] - this._lantern.y, CAM_POS[2] - this._lantern.z).normalize();
    this._beamDirs = [new THREE.Vector3(), new THREE.Vector3()];

    this._shared = {
      uLantern: { value: this._lantern.clone() },
      uBeam0: { value: new THREE.Vector3(0, 0, 1) },
      uBeam1: { value: new THREE.Vector3(0, 0, -1) },
      uBeamGain: { value: new THREE.Vector2(1, this._layout.beams > 1 ? SECOND_BEAM : 0) },
      uConeCos: { value: new THREE.Vector2(Math.cos(THREE.MathUtils.degToRad(CONE_HALF_DEG * 1.15)), Math.cos(THREE.MathUtils.degToRad(CONE_HALF_DEG * 0.75))) },
      uFog: { value: FOG_START },
      uTint: { value: this._tint },
      uAmbient: { value: 0 },
      uGlare: { value: 0 },
      uTime: { value: 0 },
      uCamera: { value: camera.position.clone() },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };

    this._buildSky();
    this._buildSea();
    this._buildCliff();
    this._buildBeams();
    this._buildParticles();
    this._buildLantern();
    this._applyPalette(null);
  }

  _add(mesh, geo, mat, order) {
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    this._objects.push(mesh);
    if (geo) this._geometries.push(geo);
    if (mat) this._materials.push(mat);
    this._scene.add(mesh);
    return mesh;
  }

  _uniforms(extra) {
    const u = {};
    for (const k of Object.keys(this._shared)) u[k] = this._shared[k];
    return Object.assign(u, extra || {});
  }

  _buildSky() {
    const geo = new THREE.PlaneGeometry(2, 2);
    this._skyUniforms = this._uniforms({ uHorizon: { value: -0.1 }, uLift: { value: 0 } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._skyUniforms, vertexShader: skyVertexShader, fragmentShader: skyFragmentShader, depthTest: false, depthWrite: false, transparent: true });
    const mesh = this._add(new THREE.Mesh(geo, mat), geo, mat, -1000);
    const self = this;
    mesh.onBeforeRender = function (renderer) { self._onFrame(renderer); };
  }

  _buildSea() {
    const geo = new THREE.PlaneGeometry(this._halfW * 2.6, SEA_NEAR - SEA_FAR, SEA_COLS, SEA_ROWS);
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, 0, (SEA_NEAR + SEA_FAR) / 2);
    this._seaUniforms = this._uniforms({ uSwell: { value: SEA_SWELL } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._seaUniforms, vertexShader: seaVertexShader, fragmentShader: seaFragmentShader, depthTest: true, depthWrite: true, transparent: true, side: THREE.DoubleSide });
    this._add(new THREE.Mesh(geo, mat), geo, mat, -900);
  }

  // The cliff's ridge, world x to y at CLIFF_Z: a plateau under the tower,
  // falling to the sea on the right, rising rough to the left edge.
  _ridge(x) {
    const xl = this._lighthouseX;
    const u = x - xl;
    let y;
    if (u > 4) y = CLIFF_TOP_Y * Math.max(0, 1 - (u - 4) / 22) * (0.75 + 0.25 * Math.cos(u * 0.6));
    else if (u > -6) y = CLIFF_TOP_Y;
    else y = CLIFF_TOP_Y + Math.min(11, (-u - 6) * 1.1) + 1.1 * Math.sin(u * 0.7) + 0.5 * Math.sin(u * 1.9);
    y += 0.25 * Math.sin(x * 1.3) + 0.12 * Math.sin(x * 3.7);
    return Math.max(0, y);
  }

  _buildCliff() {
    const rnd = lcg(0x7a11);
    const pos = [], kind = [], uv = [], idx = [];
    const push = (x, y, z, k, u, v) => { pos.push(x, y, z); kind.push(k); uv.push(u, v); return pos.length / 3 - 1; };
    // The rock: a strip from the ridge down to under the sea.
    const cols = 110;
    const x0 = -this._halfW * 1.3, x1 = this._lighthouseX + 30;
    const xl = this._lighthouseX;
    // z along the face: the tower's plane right of the tower, then coming
    // forward to the left, so the headland reaches towards the camera.
    const zAt = (x) => CLIFF_Z;
    const top = [];
    for (let i = 0; i <= cols; i++) {
      const x = x0 + (x1 - x0) * (i / cols);
      const y = this._ridge(x), z = zAt(x);
      const a = push(x, y, z, 0, i / cols, 1);
      const b = push(x, -3, z, 0, i / cols, 0);
      top.push([x, y, z]);
      if (i < cols) idx.push(a, b, a + 2, b, b + 2, a + 2);
    }
    // A nearer, darker spur of rock at the foot, for depth.
    const spur = [];
    for (let i = 0; i <= 30; i++) {
      const x = this._lighthouseX - 14 + i * 1.2;
      const y = 1.8 + 1.4 * Math.sin(i * 0.9) + 0.6 * rnd();
      spur.push([x, y, zAt(x) + 3]);
    }
    for (let i = 0; i < spur.length; i++) {
      const a = push(spur[i][0], spur[i][1], spur[i][2], 0, 0.3, 0.5);
      const b = push(spur[i][0], -3, spur[i][2], 0, 0.3, 0);
      if (i < spur.length - 1) idx.push(a, b, a + 2, b, b + 2, a + 2);
    }
    // The tower: a tapered wall, a gallery, the lantern room, the cap.
    const yb = CLIFF_TOP_Y - 0.6, zt = TOWER_Z + 0.5;
    const tw = (t) => TOWER_BASE_R + (TOWER_TOP_R - TOWER_BASE_R) * t;
    const rows = 8;
    const wall = [];
    for (let i = 0; i <= rows; i++) {
      const t = i / rows, y = yb + TOWER_H * t, w = tw(t);
      wall.push([push(xl - w, y, zt, 1, 0, t), push(xl + w, y, zt, 1, 1, t)]);
      if (i > 0) { const [a, b] = wall[i - 1], [c, d] = wall[i]; idx.push(a, b, c, b, d, c); }
    }
    const yg = yb + TOWER_H;
    const g0 = push(xl - TOWER_TOP_R * 1.6, yg, zt, 1, 0, 1), g1 = push(xl + TOWER_TOP_R * 1.6, yg, zt, 1, 1, 1);
    const g2 = push(xl - TOWER_TOP_R * 1.6, yg + 0.35, zt, 1, 0, 1), g3 = push(xl + TOWER_TOP_R * 1.6, yg + 0.35, zt, 1, 1, 1);
    idx.push(g0, g1, g2, g1, g3, g2);
    const lw = TOWER_TOP_R * 0.95;
    const l0 = push(xl - lw, yg + 0.35, zt, 2, 0, 0), l1 = push(xl + lw, yg + 0.35, zt, 2, 1, 0);
    const l2 = push(xl - lw, yg + 0.35 + LANTERN_H, zt, 2, 0, 1), l3 = push(xl + lw, yg + 0.35 + LANTERN_H, zt, 2, 1, 1);
    idx.push(l0, l1, l2, l1, l3, l2);
    const c0 = push(xl - lw * 1.2, yg + 0.35 + LANTERN_H, zt, 1, 0, 1), c1 = push(xl + lw * 1.2, yg + 0.35 + LANTERN_H, zt, 1, 1, 1), c2 = push(xl, yg + 0.35 + LANTERN_H + 1.1, zt, 1, 0.5, 1);
    idx.push(c0, c1, c2);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
    geo.setAttribute('aKind', new THREE.BufferAttribute(new Float32Array(kind), 1));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
    geo.setIndex(idx);
    this._rockUniforms = this._uniforms({ uLanternGlow: { value: 1 } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._rockUniforms, vertexShader: rockVertexShader, fragmentShader: rockFragmentShader, depthTest: true, depthWrite: true, transparent: true });
    this._add(new THREE.Mesh(geo, mat), geo, mat, -850);

    // The ridge line in a fat line, a shade above the rock, so the
    // silhouette has an edge when the beam grazes it.
    const rl = [];
    for (let i = 0; i < top.length - 1; i++) rl.push(top[i][0], top[i][1], top[i][2], top[i + 1][0], top[i + 1][1], top[i + 1][2]);
    const lgeo = new LineSegmentsGeometry();
    lgeo.setPositions(new Float32Array(rl));
    const lmat = new LineMaterial({ linewidth: 1.5, color: 0x1a1816, transparent: true, opacity: 0.9, depthTest: true, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false });
    lmat.resolution.set(1280, 720);
    this._ridgeMat = lmat;
    const lmesh = new LineSegments2(lgeo, lmat);
    this._add(lmesh, lgeo, lmat, -840);
  }

  _buildBeams() {
    this._beams = [];
    const n = this._layout.beams;
    for (let i = 0; i < n; i++) {
      const radius = Math.tan(THREE.MathUtils.degToRad(CONE_HALF_DEG)) * BEAM_LENGTH;
      const geo = new THREE.ConeGeometry(radius, BEAM_LENGTH, 28, 6, true);
      geo.translate(0, -BEAM_LENGTH / 2, 0);
      const u = this._uniforms({ uOpacity: { value: CONE_OPACITY * (i === 0 ? 1 : SECOND_BEAM) }, uFogCone: { value: FOG_START }, uFacing: { value: 1 } });
      const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: coneVertexShader, fragmentShader: coneFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: true, depthWrite: false, side: THREE.DoubleSide });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(this._lantern);
      this._add(mesh, geo, mat, -700 + i);
      this._beams.push({ mesh, uniforms: u });
    }
  }

  _particles(count, kind, order, sizeMul, gain, seedBase) {
    const rnd = lcg(seedBase);
    const base = new Float32Array(count * 3), seed = new Float32Array(count), pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      seed[i] = rnd();
      if (kind === 0) {
        base[i * 3] = (rnd() * 2 - 1) * MIST_BOX.x;
        base[i * 3 + 1] = MIST_BOX.y0 + Math.pow(rnd(), 1.4) * (MIST_BOX.y1 - MIST_BOX.y0);
        base[i * 3 + 2] = MIST_BOX.z0 + rnd() * (MIST_BOX.z1 - MIST_BOX.z0);
      } else if (kind === 1) {
        base[i * 3] = (rnd() * 2 - 1) * RAIN_BOX.x;
        base[i * 3 + 1] = rnd() * RAIN_BOX.y1;
        base[i * 3 + 2] = RAIN_BOX.z0 + rnd() * (RAIN_BOX.z1 - RAIN_BOX.z0);
      } else {
        // a velocity: up and towards the sea, spread
        const a = (rnd() - 0.5) * 1.6;
        const v = 4 + rnd() * 6;
        base[i * 3] = Math.sin(a) * v * 0.6 + (rnd() - 0.5) * 2;
        base[i * 3 + 1] = Math.cos(a) * v;
        base[i * 3 + 2] = (rnd() - 0.2) * 4;
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aBase', new THREE.BufferAttribute(base, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    const u = this._uniforms({
      uKind: { value: kind }, uPixelScale: { value: 540 }, uPointMax: { value: 64 }, uSizeMul: { value: sizeMul },
      uSprayT: { value: -1 }, uSprayOrigin: { value: new THREE.Vector3() }, uGain: { value: gain }
    });
    const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: particleVertexShader, fragmentShader: particleFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: true, depthWrite: false });
    const pts = new THREE.Points(geo, mat);
    this._add(pts, geo, mat, order);
    return { pts, uniforms: u, count };
  }

  _buildParticles() {
    const L = this._layout;
    this._mist = this._particles(L.mist, 0, -600, 1.0, 1.3, 0x51ab);
    this._rain = this._particles(L.rain, 1, -590, 1.0, 3.0, 0x2b7e);
    this._spray = this._particles(L.spray, 2, -580, 1.0, 2.0, 0x9c31);
    this._spray.uniforms.uSprayOrigin.value.set(this._lighthouseX - 16, 1.0, CLIFF_Z + 8);
  }

  _buildLantern() {
    const size = 24.0;
    const geo = new THREE.PlaneGeometry(size, size);
    this._lanternUniforms = this._uniforms({ uAngle: { value: 0 }, uGlow: { value: 1 } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._lanternUniforms, vertexShader: lanternVertexShader, fragmentShader: lanternFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(this._lantern);
    this._add(mesh, geo, mat, -500);
  }

  _onFrame(renderer) {
    renderer.getDrawingBufferSize(_size);
    const h = _size.y;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2);
    const pixelScale = (h / 2) / tanHalf;
    for (const p of [this._mist, this._rain, this._spray]) { p.uniforms.uPixelScale.value = pixelScale; p.uniforms.uPointMax.value = Math.max(12, h * 0.05); }
    this._ridgeMat.resolution.set(_size.x, h);
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length ? vivid[0] : (dominant.length ? dominant[0] : null);
    if (src) _col.set(src); else _col.setRGB(0.9, 0.8, 0.6);
    _col.getHSL(_hsl);
    _col.setHSL(_hsl.h, Math.min(0.8, _hsl.s), 0.6);
    // Seventy percent of the way to a warm white: a lighthouse is not a disco.
    this._tint.setRGB(0.98, 0.92, 0.80).lerp(_col, 0.30);
  }

  onTrackChange(profile) { this._applyPalette(profile); }

  _setCone(halfDeg) {
    this._coneHalf = halfDeg;
    this._shared.uConeCos.value.set(Math.cos(THREE.MathUtils.degToRad(halfDeg * 1.15)), Math.cos(THREE.MathUtils.degToRad(halfDeg * 0.75)));
    const s = Math.tan(THREE.MathUtils.degToRad(halfDeg)) / Math.tan(THREE.MathUtils.degToRad(CONE_HALF_DEG));
    for (const b of this._beams) b.mesh.scale.set(s, 1, s);
  }

  update(deltaTime, profile) {
    // The scene manager drifts the camera; this picture has one viewpoint.
    this._camera.position.set(CAM_POS[0], CAM_POS[1], CAM_POS[2]);
    this._camera.lookAt(CAM_LOOK[0], CAM_LOOK[1], CAM_LOOK[2]);
    this._camera.updateMatrixWorld();
    const director = profile.director || {};
    const paused = profile.playbackState !== 'PLAYING';
    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 180) : 0;
    this._beatSeconds = bpm ? 60 / bpm : FREE_TURN_SECONDS / 4;
    const beat = this._beatSeconds;
    const bands = director.bands || { low: 0, mid: 0, high: 0 };
    const measured = !!director.hasReliableGrid;
    const low = measured ? (bands.low || 0) : 0.5, mid = measured ? (bands.mid || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    const progress = profile.progress || 0;

    if (!paused) {
      const dt = Math.min(deltaTime, 0.05);
      this._time += dt;
      const t = this._time;

      // --- the clocks: the turn is the bar
      let beatEdge = false, phraseEdge = false;
      if (profile.beatPhase != null) {
        if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
        this._lastBeatPhase = profile.beatPhase;
      } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
      if (beatEdge) this._beatCount++;
      if (profile.barPhase != null) this._turn = profile.barPhase;
      else this._turn = (((this._beatCount % 4) + (profile.beatPhase != null ? profile.beatPhase : Math.min(1, (t - this._freeBeatAt) / beat))) / 4) % 1;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
      this._lastPhrase = phrase;

      // --- the drop is a fog horn
      const ev = director.nextEvent;
      let dropNow = false;
      if (ev && ev.type === 'drop' && ev.msUntil != null) {
        if (ev.msUntil <= dt * 500 + 30) dropNow = true;
        if (this._pendingDropMs != null && this._pendingDropMs < 400 && ev.msUntil > this._pendingDropMs + 250) dropNow = true;
        this._pendingDropMs = ev.msUntil;
      } else {
        if (this._pendingDropMs != null && this._pendingDropMs < 400) dropNow = true;
        this._pendingDropMs = null;
      }
      if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
      this._lastMode = director.mode;
      if (this._debugHorn && t > 2 && t - this._lastDropAt > 12 * beat) dropNow = true;
      if (dropNow && t - this._lastDropAt > 4 * beat) { this._lastDropAt = t; this._hornUntil = t + HORN_BARS * 4 * beat; }
      const horn = t < this._hornUntil ? 1 : 0;
      const targetHalf = this._forceBeamDeg != null ? this._forceBeamDeg / 2 : (horn ? HORN_HALF_DEG : CONE_HALF_DEG);
      if (Math.abs(targetHalf - this._coneHalf) > 0.01) this._setCone(this._coneHalf + (targetHalf - this._coneHalf) * Math.min(1, dt * 4));
      this._glow += ((horn ? 2.0 : 1.0) - this._glow) * Math.min(1, dt * 3);

      // --- the phrase: a bigger wave breaks on the cliff
      if (phraseEdge || (this._debugWave && t - this._sprayAt > 6)) this._sprayAt = t;

      // --- the fog lifts over the track
      const fog = this._forceFog != null ? this._forceFog : FOG_START + (FOG_END - FOG_START) * THREE.MathUtils.smoothstep(progress, 0.05, 0.95);
      this._shared.uFog.value = fog;
      const lifted = 1 - (fog - FOG_END) / (FOG_START - FOG_END);
      this._shared.uAmbient.value = AMBIENT_END * THREE.MathUtils.clamp(lifted, 0, 1);
      this._skyUniforms.uLift.value = THREE.MathUtils.clamp(lifted, 0, 1);
      for (const b of this._beams) b.uniforms.uFogCone.value = fog * 0.6;

      // --- the beams' directions
      const az = this._turn * Math.PI * 2;
      for (let i = 0; i < 2; i++) {
        const d = this._beamDirs[i];
        const a = az + (i === 1 ? Math.PI : 0);
        // the to-camera direction, turned about the vertical by a
        const cx = this._toCamera.x, cz = this._toCamera.z;
        d.set(cx * Math.cos(a) - cz * Math.sin(a), this._toCamera.y, cx * Math.sin(a) + cz * Math.cos(a)).normalize();
      }
      this._shared.uBeam0.value.copy(this._beamDirs[0]);
      this._shared.uBeam1.value.copy(this._beamDirs[1]);
      _v3b.copy(this._camera.position).sub(this._lantern).normalize();
      for (let i = 0; i < this._beams.length; i++) {
        _q.setFromUnitVectors(DOWN, this._beamDirs[i]);
        this._beams[i].mesh.quaternion.copy(_q);
        // Seen from inside, a cone's surface is a grey wall over the whole
        // frame: the mesh fades as the beam comes round to the camera.
        const c = this._beamDirs[i].dot(_v3b);
        this._beams[i].uniforms.uFacing.value = 1 - THREE.MathUtils.smoothstep(c, Math.cos(THREE.MathUtils.degToRad(26)), Math.cos(THREE.MathUtils.degToRad(10)));
      }

      // --- the glare: the beam on the camera
      _v3.copy(this._camera.position).sub(this._lantern).normalize();
      let g = 0;
      for (let i = 0; i < this._beams.length; i++) {
        const c = this._beamDirs[i].dot(_v3);
        const ang = THREE.MathUtils.radToDeg(Math.acos(THREE.MathUtils.clamp(c, -1, 1)));
        const w = 1 - THREE.MathUtils.smoothstep(ang, this._coneHalf * 0.5, this._coneHalf + GLARE_HALF_DEG);
        g = Math.max(g, w * (i === 0 ? 1 : SECOND_BEAM));
      }
      this._glareTarget = g * (0.55 + 0.45 * high) * this._glow;
      this._glare += (this._glareTarget - this._glare) * Math.min(1, dt * (this._glareTarget > this._glare ? 22 : 7));

      // --- the sea and the rain
      const seaTime = this._layout.seaMoves ? t : 0;
      this._seaUniforms.uTime.value = seaTime;
      this._seaUniforms.uSwell.value = SEA_SWELL * (0.6 + 0.8 * low);
      this._rain.pts.geometry.setDrawRange(0, Math.round(this._rain.count * (0.45 + 0.55 * mid)));
      this._spray.uniforms.uSprayT.value = t - this._sprayAt;
    }

    // --- uniforms
    this._shared.uGlare.value = this._glare;
    this._shared.uTime.value = this._time;
    this._shared.uCamera.value.copy(this._camera.position);
    this._lanternUniforms.uAngle.value = this._turn * Math.PI * 2;
    this._lanternUniforms.uGlow.value = this._glow;
    this._rockUniforms.uLanternGlow.value = this._glow;
  }

  dispose() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    this._objects = []; this._geometries = []; this._materials = [];
    this._scene = null;
  }
}
